import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getUserPreferences, updateUserPreferences } from '@/app/services/userService';
import { UserPreferenceResponse } from '@/app/types/user_preference';

interface PreferenceState {
  defaultSiteId: number | null;
  defaultGodownId: number | null;
  isPreferencesLoaded: boolean;
  actions: {
    setDefaultSiteId: (id: number | null) => void;
    setDefaultGodownId: (id: number | null) => void;
    loadPreferences: () => Promise<void>;
    savePreferences: () => Promise<UserPreferenceResponse>;
    setPreferencesLoaded: (loaded: boolean) => void;
  };
}

// Concurrent callers (React 18 StrictMode double-invokes effects, and several
// pages call loadPreferences on mount) all pass the isPreferencesLoaded check
// before the first response lands. Share the in-flight promise so they wait on
// one request instead of each firing their own.
let inFlightLoad: Promise<void> | null = null;

export const usePreferenceStore = create<PreferenceState>()(
  persist(
    (set, get) => ({
      defaultSiteId: null,
      defaultGodownId: null,
      isPreferencesLoaded: false,
      actions: {
        setDefaultSiteId: (id): void => set({ defaultSiteId: id }),
        setDefaultGodownId: (id): void => set({ defaultGodownId: id }),
        setPreferencesLoaded: (loaded): void => set({ isPreferencesLoaded: loaded }),
        loadPreferences: async (): Promise<void> => {
          if (get().isPreferencesLoaded) return; // Avoid redundant loads
          if (inFlightLoad) return inFlightLoad; // Join the request already running

          inFlightLoad = (async () => {
            try {
              const prefs = await getUserPreferences();
              set({
                defaultSiteId: prefs.default_site_id,
                defaultGodownId: prefs.default_godown_id,
                isPreferencesLoaded: true,
              });
            } catch (error) {
              console.error('Failed to load user preferences:', error);
              set({ isPreferencesLoaded: true }); // Mark as loaded even on error to prevent retry loops
            } finally {
              inFlightLoad = null;
            }
          })();

          return inFlightLoad;
        },
        savePreferences: async (): Promise<UserPreferenceResponse> => {
          try {
            const { defaultSiteId, defaultGodownId } = get();
            const payload = {
              default_site_id: defaultSiteId,
              default_godown_id: defaultGodownId,
            };
            const updatedPrefs = await updateUserPreferences(payload);
            set({
              defaultSiteId: updatedPrefs.default_site_id,
              defaultGodownId: updatedPrefs.default_godown_id,
            });
            return updatedPrefs;
          } catch (error) {
            console.error('Failed to save user preferences:', error);
            throw error;
          }
        },
      },
    }),
    {
      name: 'user-preferences-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        defaultSiteId: state.defaultSiteId,
        defaultGodownId: state.defaultGodownId,
      }),
    }
  )
);

// Export actions separately for easier usage
export const usePreferenceActions = (): PreferenceState['actions'] => usePreferenceStore((state) => state.actions); 